import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { collection, getDocs, query, orderBy, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { QRCodeCanvas } from 'qrcode.react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  InputAdornment,
  MenuItem,
  Alert,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PhoneIcon from '@mui/icons-material/Phone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

const relationOptions = [
  'Father',
  'Mother',
  'Spouse',
  'Brother',
  'Sister',
  'Friend',
  'Colleague',
  'Relative',
  'Other',
];

const RegisteredUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [activeRecord, setActiveRecord] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    vehicleNumber: '',
    contact: '',
    whatsapp: '',
    emergencyContacts: [{ name: '', number: '', relation: '' }],
  });
  const [error, setError] = useState('');

  const fetchUsers = async () => {
    setLoading(true);
    const q = query(collection(db, 'registrations'), orderBy('vehicleNumber'));
    const querySnapshot = await getDocs(q);
    setUsers(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    setLoading(false);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleDelete = async (id) => {
    const confirmed = window.confirm('Delete this registration record? This cannot be undone.');
    if (!confirmed) return;
    await deleteDoc(doc(db, 'registrations', id));
    fetchUsers();
  };

  const openEditDialog = (record) => {
    setActiveRecord(record);
    setEditForm({
      name: record.name || '',
      vehicleNumber: record.vehicleNumber || '',
      contact: record.contact || '',
      whatsapp: record.whatsapp || '',
      emergencyContacts: record.emergencyContacts?.length
        ? record.emergencyContacts
        : [{ name: '', number: '', relation: '' }],
    });
    setError('');
    setEditDialogOpen(true);
  };

  const openQrDialog = (record) => {
    setActiveRecord(record);
    setQrDialogOpen(true);
  };

  const handleEditChange = (e) => {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
  };

  const handleEmergencyChange = (idx, field, value) => {
    const updated = [...editForm.emergencyContacts];
    updated[idx][field] = value;
    setEditForm({ ...editForm, emergencyContacts: updated });
  };

  const addEmergencyContact = () => {
    setEditForm({
      ...editForm,
      emergencyContacts: [...editForm.emergencyContacts, { name: '', number: '', relation: '' }],
    });
  };

  const removeEmergencyContact = (idx) => {
    if (editForm.emergencyContacts.length === 1) return;
    setEditForm({
      ...editForm,
      emergencyContacts: editForm.emergencyContacts.filter((_, i) => i !== idx),
    });
  };

  const validateEdit = () => {
    if (!editForm.name.trim()) return 'Owner name is required.';
    const vehicleRegex = /^[A-Z]{2}[0-9]{1,2}[A-Z]{1,3}[0-9]{4}$/i;
    if (!editForm.vehicleNumber.trim() || !vehicleRegex.test(editForm.vehicleNumber.replace(/\s+/g, ''))) return 'Enter valid vehicle number.';
    if (!/^[6-9]\d{9}$/.test(editForm.contact)) return 'Enter valid contact number.';
    if (!/^[6-9]\d{9}$/.test(editForm.whatsapp)) return 'Enter valid WhatsApp number.';
    for (let i = 0; i < editForm.emergencyContacts.length; i++) {
      const ec = editForm.emergencyContacts[i];
      if (!ec.name.trim()) return `Emergency contact ${i + 1} name required`;
      if (!/^[6-9]\d{9}$/.test(ec.number)) return `Emergency contact ${i + 1} number invalid`;
      if (!ec.relation.trim()) return `Emergency contact ${i + 1} relation required`;
    }
    return '';
  };

  const handleSaveEdit = async () => {
    const errMsg = validateEdit();
    if (errMsg) {
      setError(errMsg);
      return;
    }
    const normalizedVehicleNumber = editForm.vehicleNumber.trim().toUpperCase().replace(/\s+/g, '');
    await updateDoc(doc(db, 'registrations', activeRecord.id), {
      ...editForm,
      vehicleNumber: normalizedVehicleNumber,
      updatedAt: new Date(),
    });
    setEditDialogOpen(false);
    fetchUsers();
  };

  if (loading) return <Typography>Loading registered users...</Typography>;
  if (!users.length) return <Typography>No registered users found.</Typography>;

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" gutterBottom>Registered Users</Typography>
      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: 1000 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'rgba(13, 71, 161, 0.08)' }}>
              <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', minWidth: 140 }}>Owner Name</TableCell>
              <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', minWidth: 130 }}>Vehicle Number</TableCell>
              <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', minWidth: 130 }}>Contact</TableCell>
              <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', minWidth: 130 }}>WhatsApp</TableCell>
              <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', minWidth: 200 }}>Emergency Contacts</TableCell>
              <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', minWidth: 220 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map(user => (
              <TableRow key={user.id} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 140 }}>{user.name}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 130 }}>{user.vehicleNumber}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 130 }}>{user.contact}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 130 }}>{user.whatsapp}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 200, fontSize: '0.875rem' }}>
                  {Array.isArray(user.emergencyContacts) && user.emergencyContacts.length > 0
                    ? user.emergencyContacts.map((contact, idx) => (
                        <Box key={idx} sx={{ mb: idx < user.emergencyContacts.length - 1 ? 0.5 : 0 }}>
                          {contact.name} ({contact.relation})
                        </Box>
                      ))
                    : '-'}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 220, display: 'flex', gap: 0.5 }}>
                  <Button size="small" variant="outlined" onClick={() => openEditDialog(user)}>Edit</Button>
                  <Button size="small" variant="outlined" onClick={() => openQrDialog(user)}>QR</Button>
                  <Button color="error" size="small" variant="outlined" onClick={() => handleDelete(user.id)}>Delete</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Registration</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Box component="form" sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="name"
                  label="Owner Name"
                  value={editForm.name}
                  onChange={handleEditChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="vehicleNumber"
                  label="Vehicle Number"
                  value={editForm.vehicleNumber}
                  onChange={handleEditChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="contact"
                  label="Primary Contact"
                  value={editForm.contact}
                  onChange={handleEditChange}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="whatsapp"
                  label="WhatsApp"
                  value={editForm.whatsapp}
                  onChange={handleEditChange}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <WhatsAppIcon color="success" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Emergency Contacts</Typography>
              </Grid>
              {editForm.emergencyContacts.map((contact, idx) => (
                <React.Fragment key={idx}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Name"
                      value={contact.name}
                      onChange={(e) => handleEmergencyChange(idx, 'name', e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Relation"
                      select
                      value={contact.relation}
                      onChange={(e) => handleEmergencyChange(idx, 'relation', e.target.value)}
                      fullWidth
                    >
                      {relationOptions.map((option) => (
                        <MenuItem key={option} value={option}>{option}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Number"
                      value={contact.number}
                      onChange={(e) => handleEmergencyChange(idx, 'number', e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  {editForm.emergencyContacts.length > 1 && (
                    <Grid item xs={12}>
                      <Button color="error" onClick={() => removeEmergencyContact(idx)}>
                        Remove contact
                      </Button>
                    </Grid>
                  )}
                </React.Fragment>
              ))}
              <Grid item xs={12}>
                <Button variant="outlined" onClick={addEmergencyContact}>
                  + Add emergency contact
                </Button>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={qrDialogOpen} onClose={() => setQrDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Regenerate QR</DialogTitle>
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          {activeRecord && (
            <>
              <QRCodeCanvas
                value={activeRecord.qrCodeId
                  ? `${window.location.origin}/app/#/alert/qr/${encodeURIComponent(activeRecord.qrCodeId)}`
                  : `${window.location.origin}/app/#/alert/${encodeURIComponent(activeRecord.vehicleNumber)}`}
                size={220}
                includeMargin
              />
              <Typography variant="body1" sx={{ mt: 2, fontWeight: 700 }}>
                Vehicle No: {activeRecord.vehicleNumber}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Scan this QR to go to the alert page for the registered vehicle.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RegisteredUsers;
